/**
 * Dashboard — Phase 4 + Phase 3 (workstream-scoped reports)
 *
 * Layout:
 *   Top: 4×2 grid of themed RSS news panels (8 panels total)
 *   Bottom: Daily Intelligence Brief section with workstream selector
 *
 * Panels load independently; each shows a skeleton while fetching.
 * Report section polls /api/reports/today every 5 s while generation is pending.
 */

import { api, ApiError } from "../lib/api";
import {
  isRateLimitInfo,
  rateLimitOneLiner,
  showRateLimitModal,
  type RateLimitInfo,
} from "../components/rate_limit_modal";
import { ensureAppTimezone, fmtDateTime, todayInAppTz } from "../lib/datetime";
import { registerPageCleanup } from "../lib/router";
import { getToken } from "../lib/auth";
import { renderHeader } from "../components/header";

// Ambient declarations for CDN libraries (index.html)
declare const marked: { parse: (md: string) => string };
declare const DOMPurify: { sanitize: (html: string) => string };

// ── types ──────────────────────────────────────────────────────────────────

interface FeedItem {
  headline: string;
  source: string;
  url: string;
  published_at: string;
}

interface FeedPanel {
  panel_id: string;
  label: string;
  items: FeedItem[];
  error: boolean;
  cached_at: number;
}

interface Workstream {
  id: string;
  name: string;
}

interface ReportData {
  id: string;
  reportDate: string;
  generatedAt: string;
  generatedBy: string | null;
  archived: boolean;
  content: string | null;
  workstreamId: string | null;
  workstreamName: string | null;
  /** "daily" | "weekly". Null on rows written before the cadence split. */
  kind: string | null;
  /** "draft" only while a weekly is held by the "Hold weekly as draft"
   *  setting; otherwise "sent", for both cadences. */
  status: string | null;
  sentBy: string | null;
  sentAt: string | null;
  /** Whether a PDF exists to download. False for every daily by design. */
  hasPdf: boolean;
}

interface TodayResult {
  /** "rate_limited" and "failed" are terminal states of a generation that wrote
   *  nothing — no report row, and no archiving of the one already there. They
   *  replace the old behaviour of saving the error message as that day's brief. */
  status: "pending" | "complete" | "none" | "rate_limited" | "failed";
  report?: ReportData;
  /** Why there is no brief, when the server knows. Only sent with "none", and
   *  only for the global brief. */
  lastBrief?: LastBrief;
  /** With "rate_limited" / "failed": what the attempt hit, and when. */
  detail?: string;
  attemptedAt?: string;
  rateLimit?: RateLimitInfo | null;
  /** With "complete": a *later* attempt that failed, leaving this brief in
   *  place. Without it a failed regenerate looks like a click that did nothing. */
  lastAttempt?: { kind: string; detail: string; at: string; rateLimit?: RateLimitInfo | null };
}

interface LastBrief {
  /** `generating` is not a failure: the brief outlived the two minutes the
   *  delivering job waits and is still being written. The delivery sweep
   *  replaces it with `sent` when the brief lands. */
  status: "sent" | "skipped_no_signals" | "failed" | "generating";
  date: string;
  at: string;
  detail: string | null;
}

// ── panel definitions (must match dashboard_feeds.yaml) ───────────────────

const PANELS = [
  { id: "technology",    label: "Technology" },
  { id: "cybersecurity", label: "Cybersecurity" },
  { id: "ai-policy",    label: "AI Policy & Regulation" },
  { id: "semiconductor", label: "Semiconductor & Hardware" },
  { id: "cloud",         label: "Cloud & Infrastructure" },
  { id: "ai-ml",         label: "AI/ML" },
  { id: "quantum",       label: "Quantum" },
  { id: "layoffs",       label: "Layoffs Tracker" },
];

// ── module state ───────────────────────────────────────────────────────────

let _pollInterval: ReturnType<typeof setInterval> | null = null;
let _statusSection: HTMLElement | null = null;
let _workstreams: Workstream[] = [];
let _selectedWorkstreamId = "";
/** Which cadence the brief section is showing. The weekly had no home in the
 *  UI at all before the approval gate — it was a Telegram message and a PDF —
 *  and a draft nobody can read is a gate nobody can pass. */
let _selectedKind: "daily" | "weekly" = "daily";
let _judgments: JudgmentRecord[] = [];

// ── entry point ───────────────────────────────────────────────────────────

export function renderDashboardPage(root: HTMLElement): void {
  if (_pollInterval !== null) { clearInterval(_pollInterval); _pollInterval = null; }
  // The poll outlived the page before this: navigating away left a timer asking
  // the server for report status every five seconds for the rest of the
  // session, against a _statusSection that had already been thrown away.
  registerPageCleanup(() => {
    if (_pollInterval !== null) { clearInterval(_pollInterval); _pollInterval = null; }
    _statusSection = null;
  });
  _workstreams = [];
  _selectedWorkstreamId = "";
  _selectedKind = "daily";
  _statusSection = null;

  root.appendChild(renderHeader());

  const shell = document.createElement("div");
  shell.className = "db-page";
  shell.innerHTML = `
    <div class="db-panels-grid" id="db-panels-grid"></div>
    <div class="db-report-section" id="db-report-section">
      <div class="db-report-ws-row" id="db-report-ws-row"></div>
      <div class="db-report-status" id="db-report-status">
        <div class="db-report-loading">
          <div class="db-spinner"></div>
          <span>Checking report status…</span>
        </div>
      </div>
    </div>
  `;
  root.appendChild(shell);

  const grid = root.querySelector<HTMLElement>("#db-panels-grid")!;
  _statusSection = root.querySelector<HTMLElement>("#db-report-status");

  PANELS.forEach(p => {
    const el = document.createElement("div");
    el.className = "db-panel";
    el.id = `db-panel-${p.id}`;
    el.innerHTML = `
      <div class="db-panel-header">
        <span>${esc(p.label)}</span>
        <span class="db-panel-chevron">▾</span>
      </div>
      <div class="db-panel-body" id="db-panel-body-${p.id}">
        <div class="db-skeleton"></div>
        <div class="db-skeleton db-skeleton-sm"></div>
        <div class="db-skeleton"></div>
        <div class="db-skeleton db-skeleton-sm"></div>
        <div class="db-skeleton"></div>
      </div>
    `;
    grid.appendChild(el);
    el.querySelector<HTMLElement>(".db-panel-header")!
      .addEventListener("click", () => el.classList.toggle("collapsed"));
    loadPanel(p.id, p.label);
  });

  void loadWorkstreamsAndStatus();
}

// ── workstream loading ────────────────────────────────────────────────────

async function loadWorkstreamsAndStatus(): Promise<void> {
  try {
    _workstreams = await api<Workstream[]>("/workstreams");
  } catch {
    _workstreams = [];
  }
  renderWsRow();
  void loadReportStatus();
}

function renderWsRow(): void {
  const wsRow = document.getElementById("db-report-ws-row");
  if (!wsRow) return;

  const label = document.createElement("label");
  label.className = "db-ws-label muted";
  label.htmlFor = "db-ws-select";
  label.textContent = "Workstream:";

  const select = document.createElement("select");
  select.className = "report-workstream-select";
  select.id = "db-ws-select";

  const globalOpt = document.createElement("option");
  globalOpt.value = "";
  globalOpt.textContent = "All signals (global brief)";
  select.appendChild(globalOpt);

  for (const ws of _workstreams) {
    const opt = document.createElement("option");
    opt.value = ws.id;
    opt.textContent = ws.name;
    select.appendChild(opt);
  }

  select.value = _selectedWorkstreamId;
  select.addEventListener("change", () => {
    _selectedWorkstreamId = select.value;
    if (_pollInterval !== null) { clearInterval(_pollInterval); _pollInterval = null; }
    void loadReportStatus();
  });

  // Cadence first: it decides which brief the rest of the row is about.
  const kindLabel = document.createElement("label");
  kindLabel.className = "db-ws-label muted";
  kindLabel.htmlFor = "db-kind-select";
  kindLabel.textContent = "Brief:";

  const kindSelect = document.createElement("select");
  kindSelect.className = "report-workstream-select";
  kindSelect.id = "db-kind-select";
  for (const [value, text] of [["daily", "Daily"], ["weekly", "Weekly"]] as const) {
    const opt = document.createElement("option");
    opt.value = value;
    opt.textContent = text;
    kindSelect.appendChild(opt);
  }
  kindSelect.value = _selectedKind;
  kindSelect.addEventListener("change", () => {
    _selectedKind = kindSelect.value === "weekly" ? "weekly" : "daily";
    if (_pollInterval !== null) { clearInterval(_pollInterval); _pollInterval = null; }
    void loadReportStatus();
  });

  wsRow.innerHTML = "";
  wsRow.appendChild(kindLabel);
  wsRow.appendChild(kindSelect);
  wsRow.appendChild(label);
  wsRow.appendChild(select);
}

// ── panel loading ─────────────────────────────────────────────────────────

async function loadPanel(panelId: string, label: string): Promise<void> {
  const body = document.getElementById(`db-panel-body-${panelId}`);
  if (!body) return;

  try {
    const data = await api<FeedPanel>(`/dashboard/feed/${panelId}`);

    if (data.error || data.items.length === 0) {
      body.innerHTML = feedError(panelId);
    } else {
      body.innerHTML = data.items
        .map(
          item => `
          <a class="db-headline-card" href="${esc(item.url)}" target="_blank" rel="noopener noreferrer">
            <span class="db-headline-text">${esc(item.headline)}</span>
            <span class="db-headline-source">${esc(item.source)}</span>
          </a>`
        )
        .join("");
    }
  } catch {
    body.innerHTML = feedError(panelId);
  }

  const retryBtn = body.querySelector<HTMLButtonElement>(".db-retry-btn");
  if (retryBtn) {
    retryBtn.addEventListener("click", () => {
      body.innerHTML = `
        <div class="db-skeleton"></div>
        <div class="db-skeleton db-skeleton-sm"></div>
        <div class="db-skeleton"></div>
      `;
      loadPanel(panelId, label);
    });
  }
}

function feedError(panelId: string): string {
  return `
    <div class="db-feed-error">
      <span>Feed unavailable</span>
      <button class="db-retry-btn" data-panel="${esc(panelId)}">Retry</button>
    </div>`;
}

// ── report section ────────────────────────────────────────────────────────

async function loadReportStatus(): Promise<void> {
  if (!_statusSection) return;
  try {
    _judgments = [];
    const params = new URLSearchParams({ kind: _selectedKind });
    if (_selectedWorkstreamId) params.set("workstream_id", _selectedWorkstreamId);
    const [result] = await Promise.all([
      api<TodayResult>(`/reports/today?${params}`),
      // The heading is a date; render it in the app zone or not at all.
      ensureAppTimezone(),
    ]);
    // Only for a weekly with a brief to hang them on. A failed fetch leaves the
    // list empty rather than failing the page: the brief itself already renders
    // its judgments, and this panel is the machine-readable view beside it.
    if (_selectedKind === "weekly" && result.status === "complete" && result.report) {
      try {
        _judgments = await api<JudgmentRecord[]>(
          `/reports/${encodeURIComponent(result.report.id)}/judgments`
        );
      } catch {
        _judgments = [];
      }
    }
    renderReportSection(result);
    if (result.status === "pending") startPolling();
  } catch {
    _statusSection.innerHTML = `<div class="db-report-err">Could not load report status.</div>`;
  }
}

/** One dated call a weekly made — see backend/models/judgment.py. */
interface JudgmentRecord {
  id: string;
  claim: string;
  band: string;
  /** "Likely (55-80%)" — composed server-side so nothing here has an opinion
   *  about what a band means in percentage terms. */
  bandLabel: string;
  resolveBy: string;
  citations: string[];
  outcome: string;
  resolvedAt: string | null;
}

/** What the morning brief job did, when there is no brief to show.
 *
 * "No report generated yet" is true but unhelpful on a morning the brief was
 * deliberately skipped: it reads exactly like a scheduler that died. The job
 * records its outcome (backend/brief_state.py) so this can say which it was. */
function lastBriefNote(last: LastBrief | undefined): string {
  if (!last) return "";
  const when = fmtDateTime(last.at);
  if (last.status === "skipped_no_signals") {
    return `<p class="db-report-note">No brief was sent for ${esc(last.date)} — the scan
      found no signals to report on, so none was generated. Checked ${esc(when)}.</p>`;
  }
  if (last.status === "generating") {
    // Not a failure and must not read as one — the brief is still being
    // written and the delivery sweep will send it. Anything unknown falls
    // through to "sent" below, so a new status has to be handled here or the
    // app claims a brief went out that has not.
    return `<p class="db-report-note">The brief for ${esc(last.date)} is taking
      longer than usual and is still being written — it will be delivered
      automatically. ${esc(last.detail ?? "")} (${esc(when)}).</p>`;
  }
  if (last.status === "failed") {
    return `<p class="db-report-note db-report-note-warn">The morning brief for
      ${esc(last.date)} did not go out: ${esc(last.detail ?? "generation failed")}
      (${esc(when)}).</p>`;
  }
  return "";
}

function startPolling(): void {
  if (_pollInterval !== null) return;
  const wsId = _selectedWorkstreamId;
  _pollInterval = setInterval(async () => {
    try {
      const qs = wsId ? `?workstream_id=${encodeURIComponent(wsId)}` : "";
      const r = await api<TodayResult>(`/reports/today${qs}`);
      if (r.status !== "pending") {
        clearInterval(_pollInterval!);
        _pollInterval = null;
        renderReportSection(r);
      }
    } catch { /* keep polling */ }
  }, 5000);
}

function renderReportSection(result: TodayResult): void {
  if (!_statusSection) return;
  const wsName = _workstreams.find(w => w.id === _selectedWorkstreamId)?.name;
  const scopeLabel = wsName ? ` — ${esc(wsName)}` : "";

  // A finished brief is headed with the day it was filed under, not with
  // today: a brief written just before midnight and read just after is not
  // today's, and labelling it as such is the one thing a dated document must
  // not get wrong. Every other state genuinely is about now.
  const headingDate = result.status === "complete" && result.report
    ? result.report.reportDate
    : todayInAppTz();

  const heading = `
    <div class="db-report-header">
      <h2>${_selectedKind === "weekly" ? "Weekly" : "Daily"} Brief — ${esc(headingDate)}${scopeLabel}</h2>
    </div>`;

  if (result.status === "rate_limited" || result.status === "failed") {
    const limited = result.status === "rate_limited";
    // The modal too, not just the notice: this state is usually reached while
    // the analyst is watching a spinner, and a line of small text where the
    // spinner was is easy to miss.
    if (limited && isRateLimitInfo(result.rateLimit)) showRateLimitModal(result.rateLimit);
    const reason = limited && isRateLimitInfo(result.rateLimit)
      ? rateLimitOneLiner(result.rateLimit)
      : result.detail ?? "The attempt failed.";
    _statusSection.innerHTML = `
      ${heading}
      <div class="db-report-none">
        <p class="db-report-failed">
          ${limited ? "Rate limited before the brief could be generated." : "The brief could not be generated."}
        </p>
        <p class="db-report-note db-report-note-warn">${esc(reason)}</p>
        <p class="muted db-report-note">Nothing was saved and nothing was archived,
          so a retry starts from the same place.</p>
        <button class="primary" id="db-generate-btn">Try again</button>
      </div>`;
    _statusSection.querySelector("#db-generate-btn")!
      .addEventListener("click", () => void triggerGenerate());
    return;
  }

  if (result.status === "none") {
    const noReportMsg = wsName
      ? `No ${esc(wsName)} report generated yet for today.`
      : "No report generated yet for today.";
    _statusSection.innerHTML = `
      ${heading}
      <div class="db-report-none">
        <p>${noReportMsg}</p>
        ${lastBriefNote(result.lastBrief)}
        <button class="primary" id="db-generate-btn">Generate Report</button>
      </div>`;
    _statusSection.querySelector("#db-generate-btn")!
      .addEventListener("click", () => void triggerGenerate());
    return;
  }

  if (result.status === "pending") {
    _statusSection.innerHTML = `
      ${heading}
      <div class="db-report-pending">
        <div class="db-spinner"></div>
        <span>Generating report — this may take a minute…</span>
      </div>`;
    return;
  }

  // complete
  const report = result.report!;
  const badgeHtml = report.workstreamName
    ? `<span class="report-workstream-badge">${esc(report.workstreamName)}</span>`
    : "";
  const mdHtml = renderMarkdown(report.content ?? "");

  // A regenerate that failed leaves the previous brief on screen, which reads
  // as a button that did nothing unless the failure is said out loud.
  const attempt = result.lastAttempt;
  let attemptHtml = "";
  if (attempt) {
    const line = isRateLimitInfo(attempt.rateLimit)
      ? rateLimitOneLiner(attempt.rateLimit)
      : attempt.detail;
    attemptHtml = `<p class="db-report-note db-report-note-warn">The last attempt to
      regenerate this brief did not produce one, so the version below is the earlier
      one: ${esc(line)}</p>`;
    if (attempt.kind === "rate_limited" && isRateLimitInfo(attempt.rateLimit)) {
      showRateLimitModal(attempt.rateLimit);
    }
  }

  // A held weekly. Only the weekly is ever a draft, and only while "Hold
  // weekly as draft" is on — otherwise it is delivered by the job that
  // generates it, exactly like the daily.
  //
  // This used to be the approval gate, and the copy has to change more than the
  // button does: the old note told the reader something was expected of them
  // every week. Now a draft means somebody deliberately turned holding on, so
  // the note says that and points at the setting.
  const isDraft = report.status === "draft";
  const gateHtml = isDraft
    ? `<p class="db-report-note db-report-note-warn db-draft-note">This week's brief is being
        <strong>held as a draft</strong> and has not been sent to anyone, because
        <strong>Hold weekly as draft</strong> is on in App Settings. Read it below, then
        send it when you are ready. Nothing else will send it.</p>`
    : report.sentAt
      ? `<p class="muted db-report-note">Sent — ${esc(fmtDateTime(report.sentAt))}.</p>`
      : "";

  // The calls this brief made, as rows rather than as prose. The brief already
  // renders them; this is the view that says what is still pending and when
  // each is due — the shape Phase 4's resolution queue will grow out of.
  const judgmentsHtml = _judgments.length
    ? `<details class="db-judgments" open>
        <summary>${_judgments.length} judgment${_judgments.length === 1 ? "" : "s"}</summary>
        ${_judgments
          .map(
            (j) => `<div class="db-judgment">
              <span class="ap-role-badge db-judgment-band">${esc(j.bandLabel)}</span>
              <span class="db-judgment-claim">${esc(j.claim)}</span>
              <span class="muted db-judgment-meta">by ${esc(j.resolveBy)} · ${esc(j.outcome)}</span>
            </div>`
          )
          .join("")}
       </details>`
    : "";

  _statusSection.innerHTML = `
    ${heading}
    ${attemptHtml}
    ${gateHtml}
    <div class="db-report-actions">
      ${badgeHtml}
      ${isDraft ? `<button class="primary" id="db-send-now-btn">Send now</button>` : ""}
      ${report.hasPdf ? `<button id="db-download-btn">Download PDF</button>` : ""}
      <button id="db-regenerate-btn">Regenerate</button>
      <button class="destructive-text" id="db-delete-btn">Delete</button>
    </div>
    <div class="db-report-content prose">${mdHtml}</div>
    ${judgmentsHtml}
    <details class="db-archived-section">
      <summary>Archived Reports</summary>
      <div class="db-archived-list" id="db-archived-list">Loading…</div>
    </details>`;

  wireBriefLinks(_statusSection.querySelector(".db-report-content"));

  // Wired only when it was rendered. The querySelector below used a non-null
  // assertion, so leaving this unconditional would throw on every daily.
  _statusSection.querySelector("#db-download-btn")
    ?.addEventListener("click", () => downloadPdf(report.id, report.reportDate));

  _statusSection.querySelector<HTMLButtonElement>("#db-send-now-btn")
    ?.addEventListener("click", (ev) => {
      if (!confirm("Send this weekly brief to every recipient now?")) return;
      void sendReportNow(report.id, ev.currentTarget as HTMLButtonElement);
    });

  _statusSection.querySelector("#db-regenerate-btn")!
    .addEventListener("click", () => {
      const label = wsName ? `${wsName} report` : "report";
      if (!confirm(`Regenerate today's ${label}? The current version will be archived.`)) return;
      void triggerGenerate();
    });

  _statusSection.querySelector("#db-delete-btn")!
    .addEventListener("click", () => {
      if (!confirm("Delete this report? This cannot be undone.")) return;
      void deleteReport(report.id, () => void loadReportStatus());
    });

  const details = _statusSection.querySelector("details")!;
  details.addEventListener("toggle", () => {
    if (details.open) loadArchivedReports();
  }, { once: true });
}

/** The body `POST /reports/generate` expects.
 *
 *  Typed, and that is the fix rather than a nicety. `api()` takes
 *  `body?: unknown`, so the object literal below was unchecked — it omitted
 *  `kind` entirely, the server applied its documented `"daily"` default, and
 *  every request for a weekly generated a daily instead. Nothing could catch
 *  it: `tsc` had no shape to compare against, and the server cannot tell a
 *  client that forgot the field from one that asked for a daily. An interface
 *  here is the only place the omission is visible. */
interface GenerateBody {
  workstream_id: string | null;
  kind: "daily" | "weekly";
}

async function triggerGenerate(): Promise<void> {
  if (!_statusSection) return;
  try {
    // `_selectedKind` is what the Brief dropdown sets. It has always driven the
    // *read* path; sending it here is what makes the dropdown mean something
    // when you press Generate.
    const body: GenerateBody = {
      workstream_id: _selectedWorkstreamId || null,
      kind: _selectedKind,
    };
    await api("/reports/generate", { method: "POST", body });
  } catch (err) {
    if (!(err instanceof ApiError && err.status === 409)) {
      alert(err instanceof ApiError ? err.message : "Failed to start generation");
      return;
    }
  }
  renderReportSection({ status: "pending" });
  startPolling();
}

/** Send a weekly that is being held as a draft.
 *
 *  Waits on the delivery rather than firing and forgetting — the count that
 *  comes back is the only confirmation anyone gets that it reached a chat.
 *
 *  Was `approveReport`, and the rename is the point: this is no longer the only
 *  way a weekly reaches anybody, it is the release valve for the weeks somebody
 *  chose to hold one. */
async function sendReportNow(id: string, button: HTMLButtonElement): Promise<void> {
  button.disabled = true;
  button.textContent = "Sending…";
  try {
    const res = await api<{ sent: number; failed: number }>(
      `/reports/${encodeURIComponent(id)}/send-now`,
      { method: "POST" }
    );
    alert(
      res.sent > 0
        ? `Sent to ${res.sent} recipient(s)` + (res.failed ? `, ${res.failed} failed.` : ".")
        : "Sent, but it reached nobody — check that a Telegram channel or a linked " +
          "user is configured."
    );
  } catch (err) {
    alert(err instanceof ApiError ? err.message : "Could not send the brief.");
  } finally {
    button.disabled = false;
    button.textContent = "Send now";
  }
  void loadReportStatus();
}

async function loadArchivedReports(): Promise<void> {
  const el = document.getElementById("db-archived-list");
  if (!el) return;
  try {
    const reports = await api<ReportData[]>("/reports");
    const archived = reports.filter(r => r.archived);
    if (archived.length === 0) {
      el.textContent = "No archived reports.";
      return;
    }
    el.innerHTML = archived
      .map(
        r => {
          const badge = r.workstreamName
            ? `<span class="report-workstream-badge report-workstream-badge-sm">${esc(r.workstreamName)}</span>`
            : "";
          return `
          <div class="db-archived-item">
            <span class="db-archived-date">${esc(r.reportDate)}</span>
            ${badge}
            <span class="db-archived-ts">${esc(r.generatedAt.slice(0, 16).replace("T", " "))}</span>
            ${r.hasPdf ? `<button class="db-arch-dl-btn" data-id="${esc(r.id)}" data-date="${esc(r.reportDate)}">
              Download
            </button>` : ""}
            <button class="destructive-text db-arch-del-btn" data-id="${esc(r.id)}" data-date="${esc(r.reportDate)}">
              Delete
            </button>
          </div>`;
        }
      )
      .join("");

    el.querySelectorAll<HTMLButtonElement>(".db-arch-dl-btn").forEach(btn => {
      btn.addEventListener("click", () =>
        downloadPdf(btn.dataset["id"]!, btn.dataset["date"]!)
      );
    });

    el.querySelectorAll<HTMLButtonElement>(".db-arch-del-btn").forEach(btn => {
      btn.addEventListener("click", () => {
        if (!confirm(`Delete report for ${btn.dataset["date"]}? This cannot be undone.`)) return;
        void deleteReport(btn.dataset["id"]!, () => loadArchivedReports());
      });
    });
  } catch {
    el.textContent = "Could not load archived reports.";
  }
}

// ── utilities ──────────────────────────────────────────────────────────────

async function deleteReport(reportId: string, onDone: () => void): Promise<void> {
  try {
    await api(`/reports/${reportId}`, { method: "DELETE" });
    onDone();
  } catch (err) {
    alert(err instanceof ApiError ? err.message : "Delete failed.");
  }
}

async function downloadPdf(reportId: string, reportDate: string): Promise<void> {
  const token = getToken();
  try {
    const res = await fetch(`/api/reports/${reportId}/download`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (res.status === 429) {
      // One of the two paths that bypass api(), so its 429 branch never runs
      // here. Same envelope, same modal.
      const detail = await res.json().then((d) => d?.detail).catch(() => null);
      if (isRateLimitInfo(detail)) {
        showRateLimitModal(detail);
        return;
      }
    }
    if (!res.ok) {
      // The server distinguishes "this brief has no PDF" from "the file is
      // missing from disk", and that wording was thrown away by reporting the
      // bare status code.
      const detail = await res.json().then((d) => d?.detail).catch(() => null);
      throw new Error(typeof detail === "string" && detail ? detail : `HTTP ${res.status}`);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `kassandra-brief-${reportDate}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (err) {
    alert(`Download failed: ${err instanceof Error ? err.message : String(err)}`);
  }
}

/** Every link in a brief points at an original article, so every one opens in a
 *  new tab and drops the opener reference.
 *
 *  Done here rather than by configuring DOMPurify's attribute allowlist: the
 *  sanitiser arrives from a CDN and its defaults are its own business, and a
 *  security property that depends on a third party's configuration staying
 *  where you last saw it is not one you can rely on. `rel` matters as much as
 *  `target` — without it the opened page gets a live `window.opener` handle
 *  back to the app. */
function wireBriefLinks(container: Element | null): void {
  container?.querySelectorAll("a[href]").forEach((link) => {
    link.setAttribute("target", "_blank");
    link.setAttribute("rel", "noopener noreferrer");
  });
}

function renderMarkdown(content: string): string {
  if (typeof marked !== "undefined") {
    try {
      const html = marked.parse(content) as string;
      return typeof DOMPurify !== "undefined" ? DOMPurify.sanitize(html) : html;
    } catch { /* fall through */ }
  }
  return `<pre class="db-report-pre">${esc(content)}</pre>`;
}

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
